import { useEffect, useRef, useState } from 'react';
import type { RespondentField } from '../../lib/multimodal-contract';
import { captureException } from '../../lib/telemetry/posthog/frontend';
import { InformationBlockMount } from './InformationBlockMount';

/** Passive spoken content uses the shared stage and the existing presentation receipt. */
interface SpokenInformationTileProps {
  information: NonNullable<RespondentField['information']>;
  onPresented?: (fieldId: string) => Promise<boolean>;
  onAspectRatio: (ratio: number) => void;
}

/** A render is acknowledged only after its content can actually be seen. */
export function SpokenInformationTile({ information, onPresented, onAspectRatio }: SpokenInformationTileProps) {
  const elementRef = useRef<HTMLElement>(null);
  const attempted = useRef(false);
  const [imageReady, setImageReady] = useState(!information.imageUrl);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    const element = elementRef.current;
    if (!element || !imageReady || !onPresented || attempted.current) return undefined;
    let active = true;
    /** Hidden tabs, collapsed panels and failed images cannot satisfy presentation. */
    const confirmWhenVisible = () => {
      if (!active || attempted.current || document.visibilityState === 'hidden'
        || element.closest('[aria-hidden="true"], [inert]')) return;
      const rect = element.getBoundingClientRect();
      if (rect.width <= 0 || rect.height <= 0 || rect.bottom <= 0 || rect.right <= 0
        || rect.top >= window.innerHeight || rect.left >= window.innerWidth) return;
      attempted.current = true;
      void (async () => {
        try {
          const confirmed = await onPresented(information.fieldId);
          if (active && !confirmed) setFailed(true);
        } catch (error) {
          captureException(error instanceof Error ? error : new Error(String(error)), 'SpokenInformationTile.presented', { fieldId: information.fieldId });
          if (active) setFailed(true);
        }
      })();
    };
    const frame = requestAnimationFrame(confirmWhenVisible);
    const observer = typeof IntersectionObserver === 'function' ? new IntersectionObserver(confirmWhenVisible) : null;
    observer?.observe(element);
    window.addEventListener('resize', confirmWhenVisible);
    document.addEventListener('visibilitychange', confirmWhenVisible);
    return () => {
      active = false;
      cancelAnimationFrame(frame);
      observer?.disconnect();
      window.removeEventListener('resize', confirmWhenVisible);
      document.removeEventListener('visibilitychange', confirmWhenVisible);
    };
  }, [imageReady, information.fieldId, onPresented, attempt]);

  return <section ref={elementRef} aria-label="Shared information" className="relative h-full w-full min-h-0 min-w-0">
    {information.imageUrl ? <InformationBlockMount key={attempt} block={{ imageUrl: information.imageUrl, title: information.title,
      imageStillUrl: information.imageStillUrl, imageSource: information.imageSource }} onAspectRatio={onAspectRatio}
      onImageReady={() => setImageReady(true)} onImageError={() => {
        setFailed(true);
        captureException(new Error('Shared information image could not load'), 'SpokenInformationTile.image', { fieldId: information.fieldId });
      }} />
      : <div className="h-full overflow-y-auto rounded-xl border border-border bg-surface-raised p-5 md:p-8">
        {information.title ? <h2 className="text-lg font-semibold leading-snug text-foreground">{information.title}</h2> : null}
        {information.body ? <p className="mt-3 whitespace-pre-wrap break-words text-base leading-relaxed text-foreground">{information.body}</p> : null}
      </div>}
    {failed ? <button type="button" className="absolute bottom-3 right-3 rounded-lg bg-surface px-3 py-2 text-sm text-foreground focus-visible:outline-1 focus-visible:-outline-offset-1 focus-visible:outline-ring"
      onClick={() => { attempted.current = false; setFailed(false); setImageReady(!information.imageUrl); setAttempt(value => value + 1); }}>Retry</button> : null}
  </section>;
}
